import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  memo,
  forwardRef,
  useRef,
} from 'react'
import { useMusic } from '../context/MusicContext'
import { FaPlay, FaPause, FaStepForward, FaStepBackward } from 'react-icons/fa'
import { MdQueueMusic } from 'react-icons/md'
import { IoClose } from 'react-icons/io5'
import { FiRepeat } from 'react-icons/fi'
import { useContainerSize } from '../hooks/useContainerSize'
import CoverImage from './CoverImage'
import ProgressBar from './ProgressBar'
import { buildMediaUrl } from '../utils/buildMediaUrl'
import {
  clearMediaSession,
  loadTrackIntoAudio,
  requestAudioPlay,
  setMediaSessionHandlers,
  updateMediaSessionMetadata,
  updateMediaSessionPlaybackState,
  updateMediaSessionPosition,
} from './player/playbackController'

const SongInfo = React.memo(({ currentTrack }) => (
  <div className="grid-item-playing-info">
    <div className="align-center">
      <span className="song-title">
        {currentTrack
          ? `${currentTrack.title} - ${currentTrack.creator}`
          : '...'}
      </span>
    </div>
  </div>
))

function trackMatchesPlaylistItem(track, item) {
  if (!track || !item) return false
  if (track.type === 'music' && item.type === 'music') {
    return track.title === item.title && track.creator === item.artist
  }
  if (track.type === 'audio-book' && item.type === 'audio-book') {
    return track.title === item.chapter && track.creator === item.book
  }
  return false
}

function buildTrackKey(track) {
  if (!track) return ''
  return `${track.type}|${track.creator}|${track.title}`
}

function playlistItemToCurrentTrack(item) {
  if (!item) return null
  if (item.type === 'audio-book') {
    return { type: 'audio-book', creator: item.book, title: item.chapter }
  }
  if (item.type === 'music') {
    return { type: 'music', creator: item.artist, title: item.title }
  }
  return null
}

function resolvePlaybackIndex(activePlaylist, currentTrack, storedIndex) {
  if (storedIndex >= 0 && storedIndex < activePlaylist.length) {
    if (!currentTrack) return storedIndex
    if (trackMatchesPlaylistItem(currentTrack, activePlaylist[storedIndex])) {
      return storedIndex
    }
  }
  if (!currentTrack || activePlaylist.length === 0) return -1
  return activePlaylist.findIndex((item) =>
    trackMatchesPlaylistItem(currentTrack, item),
  )
}

const MusicPlayerInner = (
  { onExpand, onClose, onToggleQueue, isQueueOpen, isExpanded },
  playerRef,
) => {
  const prevButtonRef = useRef(null)
  const playButtonRef = useRef(null)
  const nextButtonRef = useRef(null)

  useContainerSize(prevButtonRef, {
    scale: 0.7,
    cssVar: '--btn-size',
    target: 'button',
  })
  useContainerSize(playButtonRef, {
    scale: 0.9,
    cssVar: '--btn-size',
    target: 'button',
  })
  useContainerSize(nextButtonRef, {
    scale: 0.7,
    cssVar: '--btn-size',
    target: 'button',
  })

  const {
    stopMusic,
    clearUserPlaylist,
    currentTrack,
    updateCurrentTrack,
    audioRef,
    isPlaying,
    setIsPlaying,
    userPlaylist,
    fallbackPlaylist,
    currentSongIndex,
    setCurrentSongIndex,
    isLooping,
    setIsLooping,
    API_URL,
    resetPlayerAndQueues,
  } = useMusic()

  const activePlaylist = useMemo(
    () => (userPlaylist.length > 0 ? userPlaylist : fallbackPlaylist),
    [userPlaylist, fallbackPlaylist],
  )

  const [isLoading, setIsLoading] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  // Mirrors for async callbacks — avoids stale closures without adding deps
  const currentSongIndexRef = useRef(currentSongIndex)
  currentSongIndexRef.current = currentSongIndex
  const isPlayingRef = useRef(isPlaying)
  isPlayingRef.current = isPlaying

  // Guards
  const trackLoadGenerationRef = useRef(0) // cancels superseded loads on rapid track changes
  const isScrubbingRef = useRef(false) // suppresses time updates while user drags progress bar
  const isTrackLoadingRef = useRef(false) // suppresses false pause events fired during audio.load()

  // Prevents re-loading a track that this controller already loaded
  const prevTrackKeyRef = useRef('')
  const controllerManagedTrackKeyRef = useRef('')

  // Throttles media session position sync to ~1 Hz
  const lastPositionSyncRef = useRef(0)

  // Stable refs for Media Session action handlers — updated each render so handlers
  // always call the latest function, but the effect that registers them runs only once.
  // This prevents React re-renders from briefly nulling out the lock-screen controls.
  const mediaSessionPlayRef = useRef(null)
  const mediaSessionPauseRef = useRef(null)
  const mediaSessionNextRef = useRef(null)
  const mediaSessionPrevRef = useRef(null)

  const trackKey = buildTrackKey(currentTrack)
  const canControlPlayback = Boolean(currentTrack && activePlaylist.length > 0)

  const setAudioNode = useCallback(
    (node) => {
      audioRef.current = node
    },
    [audioRef],
  )

  const runTrackLoad = useCallback(
    async (track, { autoplay = false, reason = 'unknown' } = {}) => {
      trackLoadGenerationRef.current += 1
      const gen = trackLoadGenerationRef.current

      try {
        if (!track) return { ok: false, skipped: true }

        setCurrentTime(0)
        setDuration(0)
        setIsLoading(true)
        isTrackLoadingRef.current = true

        const audio = audioRef.current
        if (!audio) return { ok: false, skipped: true }

        const result = await loadTrackIntoAudio({
          audio,
          track,
          API_URL,
          generation: gen,
          generationRef: trackLoadGenerationRef,
          autoplay,
          reason,
        })

        if (
          autoplay &&
          trackLoadGenerationRef.current === gen &&
          result &&
          !result.ok
        ) {
          setIsPlaying(false)
        }

        return result
      } catch (error) {
        if (trackLoadGenerationRef.current === gen) {
          console.error('Playback error:', error)
          setIsPlaying(false)
        }
        return { ok: false, error }
      } finally {
        if (trackLoadGenerationRef.current === gen) {
          setIsLoading(false)
          isTrackLoadingRef.current = false
        }
      }
    },
    [API_URL, audioRef, setIsPlaying],
  )

  const handleTimeUpdate = (event) => {
    const audio = event?.currentTarget || audioRef.current
    if (!audio || audio !== audioRef.current) return

    if (!isScrubbingRef.current) {
      setCurrentTime(audio.currentTime)

      const now = Date.now()
      if (now - lastPositionSyncRef.current > 1000) {
        updateMediaSessionPosition(audio)
        lastPositionSyncRef.current = now
      }
    }
  }

  const handleLoadedMetadata = (event) => {
    const audio = event?.currentTarget || audioRef.current
    if (!audio || audio !== audioRef.current) return

    setDuration(audio.duration)
    updateMediaSessionPosition(audio)
  }

  const handleProgressSeek = useCallback(
    (boundedTime) => {
      if (!audioRef.current) return
      try {
        audioRef.current.currentTime = boundedTime
        setCurrentTime(boundedTime)
        updateMediaSessionPosition(audioRef.current)
      } catch (error) {
        console.error('Error updating audio time:', error)
      }
    },
    [audioRef],
  )

  const handleProgressDragChange = useCallback((dragging) => {
    isScrubbingRef.current = dragging
  }, [])

  const playTrackAtIndex = useCallback(
    async (targetIndex, { autoplay = true, reason = 'manual-select' } = {}) => {
      if (activePlaylist.length === 0) return

      const nextItem = activePlaylist[targetIndex]
      const nextTrack = playlistItemToCurrentTrack(nextItem)
      if (!nextTrack) return

      controllerManagedTrackKeyRef.current = buildTrackKey(nextTrack)
      currentSongIndexRef.current = targetIndex
      setCurrentSongIndex(targetIndex)
      updateCurrentTrack(nextItem)
      setIsPlaying(autoplay)

      await runTrackLoad(nextTrack, { autoplay, reason })
    },
    [
      activePlaylist,
      runTrackLoad,
      setCurrentSongIndex,
      setIsPlaying,
      updateCurrentTrack,
    ],
  )

  const navigatePlaylist = useCallback(
    async (direction, { autoplay = true, reason = 'navigation' } = {}) => {
      if (activePlaylist.length === 0) return

      const baseIndex = resolvePlaybackIndex(
        activePlaylist,
        currentTrack,
        currentSongIndexRef.current,
      )

      const nextIndex =
        (baseIndex + direction + activePlaylist.length) % activePlaylist.length

      console.log('navigatePlaylist called with direction:', direction)
      console.log('baseIndex:', baseIndex)
      console.log('nextIndex:', nextIndex)
      console.log('reason:', reason)

      if (
        // userPlaylist.length > 0 &&
        reason !== 'user-pressed-next' &&
        baseIndex !== -1 &&
        nextIndex === 0 &&
        direction === 1 &&
        !isLooping
      ) {
        console.log('Reached the end of the playlist. Stopping playback.')
        clearUserPlaylist()
        stopMusic()
        return
      } else {
        console.log('Navigating to next track.')
      }

      await playTrackAtIndex(nextIndex, { autoplay, reason })
    },
    [
      activePlaylist,
      clearUserPlaylist,
      currentTrack,
      isLooping,
      playTrackAtIndex,
      stopMusic,
      userPlaylist.length,
    ],
  )

  const requestPlay = useCallback(
    async ({ reason = 'play-request' } = {}) => {
      console.log('[DEBUG:requestPlay-start]', {
        reason,
        audioPaused: audioRef.current?.paused,
        audioMuted: audioRef.current?.muted,
        currentTime: audioRef.current?.currentTime,
        readyState: audioRef.current?.readyState,
        networkState: audioRef.current?.networkState,
        currentTrack,
      })
      const audio = audioRef.current
      const activeIndex = resolvePlaybackIndex(
        activePlaylist,
        currentTrack,
        currentSongIndexRef.current,
      )

      if (!currentTrack) {
        if (activePlaylist.length === 0) return
        const fallbackIndex = activeIndex >= 0 ? activeIndex : 0
        await playTrackAtIndex(fallbackIndex, { autoplay: true, reason })
        return
      }

      const expectedSrc = buildMediaUrl(currentTrack, API_URL, 'audio')
      if (!audio || !expectedSrc || audio.src !== expectedSrc) {
        await runTrackLoad(currentTrack, { autoplay: true, reason })
        return
      }

      // Try playing the already-loaded audio directly
      setIsPlaying(true)
      const didPlay = await requestAudioPlay(audio, { reason, trackKey })
      console.log('[DEBUG:requestPlay-result]', {
        didPlay,
        paused: audio?.paused,
        muted: audio?.muted,
        currentTime: audio?.currentTime,
        readyState: audio?.readyState,
        networkState: audio?.networkState,
      })
      if (didPlay) return

      // play() failed — audio may have been evicted (iOS background); reload and retry
      console.log('[DEBUG:requestPlay-reload-fallback]', {
        reason,
        src: audio?.src,
        currentSrc: audio?.currentSrc,
        readyState: audio?.readyState,
        networkState: audio?.networkState,
      })
      setIsPlaying(false)
      await runTrackLoad(currentTrack, {
        autoplay: true,
        reason: `${reason}-fallback`,
      })
    },
    [
      API_URL,
      activePlaylist,
      audioRef,
      currentTrack,
      playTrackAtIndex,
      runTrackLoad,
      setIsPlaying,
      trackKey,
    ],
  )

  const requestPause = useCallback(() => {
    const audio = audioRef.current
    if (audio) audio.pause()
    setIsPlaying(false)
    updateMediaSessionPlaybackState('paused')
    updateMediaSessionMetadata(currentTrack, API_URL)
    if (audio) updateMediaSessionPosition(audio)
    console.log('[DEBUG:mediaSession-reassert-paused]', { via: 'requestPause' })
  }, [API_URL, audioRef, currentTrack, setIsPlaying])

  const playPause = useCallback(() => {
    if (!currentTrack && activePlaylist.length === 0) return

    if (
      audioRef.current &&
      !audioRef.current.paused &&
      !audioRef.current.ended
    ) {
      requestPause()
      return
    }

    requestPlay({ reason: 'toggle-playback' })
  }, [activePlaylist.length, audioRef, currentTrack, requestPause, requestPlay])

  const playNext = useCallback(
    // console.log('playNext called'),
    (options = {}) => {
      const normalizedOptions =
        options && typeof options === 'object' && !options.nativeEvent
          ? options
          : {}

      if (activePlaylist.length === 0) return

      if (isLooping) {
        const audio = audioRef.current
        if (!audio || audio.currentTime <= 3) return
        audio.currentTime = 0
        setCurrentTime(0)
        updateMediaSessionPosition(audio)
        return
      }

      console.log('will call navigatePlaylist')
      return navigatePlaylist(1, {
        autoplay: true,
        reason:
          normalizedOptions.reason || normalizedOptions.source || 'next-track',
      })
    },
    [activePlaylist.length, audioRef, isLooping, navigatePlaylist],
  )

  const playPrevious = useCallback(() => {
    if (
      currentTrack &&
      audioRef.current &&
      audioRef.current.currentTime > 2 &&
      !isLoading
    ) {
      audioRef.current.currentTime = 0
      setCurrentTime(0)
      updateMediaSessionPosition(audioRef.current)
      return
    }

    if (!currentTrack || activePlaylist.length === 0 || isLooping) return

    return navigatePlaylist(-1, { autoplay: true, reason: 'previous-track' })
  }, [
    activePlaylist.length,
    audioRef,
    currentTrack,
    isLoading,
    isLooping,
    navigatePlaylist,
  ])

  // Keep Media Session handler refs current on every render
  mediaSessionPlayRef.current = requestPlay
  mediaSessionPauseRef.current = requestPause
  mediaSessionNextRef.current = playNext
  mediaSessionPrevRef.current = playPrevious

  const toggleLoop = () => {
    setIsLooping((prev) => !prev)
    if (audioRef.current) audioRef.current.loop = !isLooping
  }

  const handleCoverClick = useCallback(() => {
    if (!currentTrack) return
    onExpand()
  }, [currentTrack, onExpand])

  const handleClosePlayer = useCallback(() => {
    onClose?.()
    clearMediaSession()
    resetPlayerAndQueues()
  }, [onClose, resetPlayerAndQueues])

  const handleEnded = useCallback(() => {
    const audio = audioRef.current
    console.log('[DEBUG:handleEnded]', {
      currentTime: audio?.currentTime,
      duration: audio?.duration,
      ended: audio?.ended,
      src: audio?.currentSrc,
    })
    Promise.resolve(playNext({ reason: 'audio-ended' })).catch((error) =>
      console.error('Unable to continue playback after track ended:', error),
    )
  }, [audioRef, playNext])

  const handleAudioPause = useCallback(
    (event) => {
      const audio = event.currentTarget
      if (audio !== audioRef.current) return
      // console.log('[DEBUG:handleAudioPause]', {
      //   ended: audio.ended,
      //   currentTime: audio.currentTime,
      //   duration: audio.duration,
      //   isTrackLoading: isTrackLoadingRef.current,
      // })
      if (isTrackLoadingRef.current) return
      if (!audio.ended) setIsPlaying(false)
      updateMediaSessionPlaybackState('paused')
      updateMediaSessionMetadata(currentTrack, API_URL)
      updateMediaSessionPosition(audio)
      // console.log('[DEBUG:mediaSession-reassert-paused]', {
      //   via: 'handleAudioPause',
      // })
    },
    [API_URL, audioRef, currentTrack, setIsPlaying],
  )

  const handleAudioPlay = useCallback(
    (event) => {
      const audio = event.currentTarget
      if (audio !== audioRef.current) return
      setIsPlaying(true)
      updateMediaSessionPlaybackState('playing')
      updateMediaSessionPosition(audio)
    },
    [audioRef, setIsPlaying],
  )

  const memoizedCoverImage = useMemo(
    () => (
      <CoverImage
        currentTrack={currentTrack}
        API_URL={API_URL}
        onClick={handleCoverClick}
        isExpanded={isExpanded}
        className="grid-item-cover"
      />
    ),
    [currentTrack, API_URL, isExpanded, handleCoverClick],
  )

  // Load track when currentTrack changes from outside this component
  useEffect(() => {
    if (!trackKey) {
      // console.log('[DEBUG:Effect1-trackKey-empty]', new Error().stack)
      prevTrackKeyRef.current = ''
      controllerManagedTrackKeyRef.current = ''

      const audio = audioRef.current
      if (audio) {
        audio.pause()
        audio.removeAttribute('src')
        audio.load()
      }

      setCurrentTime(0)
      setDuration(0)
      setIsLoading(false)
      clearMediaSession()
      return
    }

    if (prevTrackKeyRef.current === trackKey) return
    prevTrackKeyRef.current = trackKey

    // Skip reload when this controller already loaded the track (e.g. via playTrackAtIndex)
    if (controllerManagedTrackKeyRef.current === trackKey) {
      controllerManagedTrackKeyRef.current = ''
      return
    }

    // console.log('[DEBUG:Effect1-runTrackLoad]', {
    //   autoplay: isPlaying,
    //   trackKey,
    // })
    runTrackLoad(currentTrack, {
      autoplay: isPlaying,
      reason: 'external-track-change',
    })
  }, [audioRef, currentTrack, isPlaying, runTrackLoad, trackKey])

  useEffect(() => {
    updateMediaSessionMetadata(currentTrack, API_URL)
  }, [API_URL, currentTrack])

  useEffect(() => {
    // console.log('[DEBUG:Effect3] registering Media Session handlers')
    const cleanup = setMediaSessionHandlers({
      onPlay: () => {
        // console.log('[DEBUG:mediaSession-action]', { action: 'play' })
        mediaSessionPlayRef.current?.({ reason: 'media-session-play' })
      },
      onPause: () => {
        // console.log('[DEBUG:mediaSession-action]', { action: 'pause' })
        mediaSessionPauseRef.current?.()
      },
      onNext: () => {
        // console.log('[DEBUG:mediaSession-action] next')
        mediaSessionNextRef.current?.({ reason: 'media-session-next' })
      },
      onPrevious: () => {
        console.log('[DEBUG:mediaSession-action] previous')
        mediaSessionPrevRef.current?.()
      },
    })
    return () => {
      console.log('[DEBUG:Effect3-cleanup] removing Media Session handlers')
      cleanup()
    }
  }, []) // stable refs — handlers registered once, never torn down by re-renders

  useEffect(() => {
    updateMediaSessionPlaybackState(isPlaying ? 'playing' : 'paused')
  }, [isPlaying])

  // Resume playback when the app returns to the foreground (tab/screen becomes visible)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const audio = audioRef.current
        if (isPlayingRef.current && audio && audio.paused && !audio.ended) {
          requestPlay({ reason: 'visibility-resume' })
        }
        updateMediaSessionPlaybackState(
          isPlayingRef.current ? 'playing' : 'paused',
        )
        if (audio) updateMediaSessionPosition(audio)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [audioRef, requestPlay])

  useEffect(() => () => clearMediaSession(), [])

  return (
    <>
      <audio
        ref={setAudioNode}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onPause={handleAudioPause}
        onPlay={handleAudioPlay}
        loop={isLooping}
        preload="auto"
      />
      <div ref={playerRef} className="music-player-container">
        <button
          type="button"
          className="player-close-button"
          title="Stäng spelaren"
          aria-label="Stäng spelaren"
          onClick={handleClosePlayer}
        >
          <IoClose size={22} />
        </button>
        <button
          type="button"
          className={`player-queue-button${isQueueOpen ? ' active' : ''}`}
          title="Visa kön"
          aria-label={isQueueOpen ? 'Dölj kön' : 'Visa kön'}
          aria-pressed={isQueueOpen}
          onClick={onToggleQueue}
        >
          <MdQueueMusic size={24} />
        </button>
        {memoizedCoverImage}
        <SongInfo currentTrack={currentTrack} />
        <div className="grid-item-controls">
          <div className="playback-buttons-container prev" ref={prevButtonRef}>
            <button
              title="Rewind Button"
              onClick={playPrevious}
              disabled={!canControlPlayback}
            >
              <FaStepBackward />
            </button>
          </div>
          <div className="playback-buttons-container play" ref={playButtonRef}>
            <button
              title="Play/Pause Button"
              onClick={playPause}
              disabled={isLoading || !canControlPlayback}
            >
              {isPlaying ? <FaPause /> : <FaPlay />}
            </button>
          </div>
          <div className="playback-buttons-container next" ref={nextButtonRef}>
            <button
              title="Forward Button"
              onClick={() => playNext({ reason: 'user-pressed-next' })}
              disabled={!canControlPlayback}
            >
              <FaStepForward />
            </button>
          </div>
        </div>
        <div className="grid-item-repeat-control">
          <button
            title="Loop"
            onClick={toggleLoop}
            className={`loop-button${isLooping ? ' active' : ''}`}
            disabled={!canControlPlayback}
          >
            <FiRepeat size={24} />
          </button>
        </div>
        <div className="grid-item-progress">
          <ProgressBar
            currentTime={currentTime}
            duration={duration}
            onSeek={handleProgressSeek}
            onDragChange={handleProgressDragChange}
          />
        </div>
      </div>
    </>
  )
}

const MusicPlayer = memo(forwardRef(MusicPlayerInner))

export default MusicPlayer
